import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../App';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Stethoscope, RefreshCw, AlertTriangle, CheckCircle2, Search, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const severityStyles = {
  critical: 'border-red-500/40 bg-red-500/10 text-red-200',
  warning: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
  ok: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
};

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-3" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
    <div className="mt-1 text-2xl font-semibold text-white">{value}</div>
  </div>
);

export default function SuperAdminScoringDiagnostics() {
  const { authAxios } = useContext(AppContext);
  const [seasons, setSeasons] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    loadSeasons();
  }, []);

  const loadSeasons = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`${API}/platform/scoring-diagnostics/seasons`);
      setSeasons(data.seasons);
      setDuplicates(data.duplicate_season_numbers);
    } catch (e) {
      toast.error('Failed to load seasons');
    } finally {
      setLoading(false);
    }
  };

  const inspect = async (seasonId) => {
    setSelected(seasonId);
    setDetailLoading(true);
    try {
      const { data } = await authAxios.get(`${API}/platform/scoring-diagnostics/${seasonId}`);
      setDetail(data);
    } catch (e) {
      toast.error('Failed to run diagnostics');
    } finally {
      setDetailLoading(false);
    }
  };

  const recalculate = async () => {
    setRecalculating(true);
    try {
      const { data } = await authAxios.post(`${API}/platform/scoring-diagnostics/${selected}/recalculate`);
      toast.success(`Rebuilt ${data.scorecard_count} scorecard and ${data.leaderboard_count} leaderboard entries`);
      await inspect(selected);
      await loadSeasons();
    } catch (e) {
      toast.error('Recalculate failed');
    } finally {
      setRecalculating(false);
    }
  };

  const visible = seasons.filter((s) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      (s.league_name || '').toLowerCase().includes(q) ||
      String(s.number || '').includes(q) ||
      (s.title || '').toLowerCase().includes(q)
    );
  });

  const dupKeys = new Set(duplicates.flatMap((d) => d.season_ids));

  return (
    <SuperAdminLayout>
      <div className="space-y-8" data-testid="scoring-diagnostics-page">
        <div>
          <h1 className="flex items-center gap-3 text-3xl font-semibold text-white">
            <Stethoscope className="h-7 w-7 text-amber-400" />
            Scoring Diagnostics
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            Pick any season in any league to see whether its points actually add up — events, rosters,
            scorecard and leaderboard side by side, with the reason anything is missing.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,420px)_1fr]">
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by league or season number"
                className="pl-9"
                data-testid="diagnostics-season-filter"
              />
            </div>

            {loading ? (
              <div className="flex items-center gap-2 py-8 text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading seasons…
              </div>
            ) : (
              <div className="max-h-[70vh] space-y-2 overflow-y-auto pr-1" data-testid="diagnostics-season-list">
                {visible.map((s) => (
                  <button
                    key={s.season_id}
                    onClick={() => inspect(s.season_id)}
                    data-testid={`diagnostics-season-${s.season_id}`}
                    className={`w-full rounded-lg border px-4 py-3 text-left transition-colors ${
                      selected === s.season_id
                        ? 'border-amber-400/60 bg-amber-400/10'
                        : 'border-white/10 bg-white/5 hover:border-white/25'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-white">
                        Season {s.number} · {s.league_name}
                      </span>
                      {dupKeys.has(s.season_id) && (
                        <Badge variant="destructive" className="text-[10px]">duplicate</Badge>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-slate-400">
                      {s.counts.events} events · {s.counts.rosters} rosters · {s.counts.leaderboard_entries} leaderboard · {s.status}
                    </div>
                  </button>
                ))}
                {!visible.length && <div className="py-6 text-sm text-slate-500">No seasons match.</div>}
              </div>
            )}
          </div>

          <div>
            {detailLoading && (
              <div className="flex items-center gap-2 py-8 text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Running diagnostics…
              </div>
            )}

            {!detailLoading && !detail && (
              <div className="rounded-lg border border-dashed border-white/15 px-6 py-16 text-center text-sm text-slate-500">
                Select a season on the left to inspect its scoring.
              </div>
            )}

            {!detailLoading && detail && (
              <div className="space-y-6" data-testid="diagnostics-detail">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-white">
                      Season {detail.season.number} — {detail.season.league_name}
                    </h2>
                    <p className="text-sm text-slate-400">
                      {detail.season.title || 'Untitled'} · status {detail.season.status}
                    </p>
                  </div>
                  <Button
                    onClick={recalculate}
                    disabled={recalculating}
                    data-testid="diagnostics-recalculate-btn"
                    className="bg-amber-500 text-slate-900 hover:bg-amber-400"
                  >
                    {recalculating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                    Recalculate this season
                  </Button>
                </div>

                <div className="space-y-2" data-testid="diagnostics-issues">
                  {detail.issues.map((issue, i) => (
                    <div
                      key={i}
                      className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${severityStyles[issue.severity] || severityStyles.warning}`}
                    >
                      {issue.severity === 'ok' ? (
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                      ) : (
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      )}
                      <span>{issue.message}</span>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <Stat label="Events" value={detail.counts.events} testid="stat-events" />
                  <Stat label="Rosters" value={detail.counts.rosters} testid="stat-rosters" />
                  <Stat label="Scorecard rows" value={detail.counts.scorecard_entries} testid="stat-scorecard" />
                  <Stat label="Leaderboard rows" value={detail.counts.leaderboard_entries} testid="stat-leaderboard" />
                  <Stat label="Episodes" value={detail.counts.episodes} testid="stat-episodes" />
                  <Stat label="Contestants" value={detail.counts.contestants} testid="stat-contestants" />
                  <Stat label="Stored points" value={detail.counts.stored_total_points} testid="stat-stored-points" />
                  <Stat label="Expected points" value={detail.counts.expected_total_points} testid="stat-expected-points" />
                </div>

                <div className="overflow-x-auto rounded-lg border border-white/10">
                  <table className="w-full text-sm" data-testid="diagnostics-players-table">
                    <thead className="bg-white/5 text-xs uppercase tracking-wider text-slate-400">
                      <tr>
                        <th className="px-4 py-3 text-left">Player</th>
                        <th className="px-4 py-3 text-left">Type</th>
                        <th className="px-4 py-3 text-right">Rosters</th>
                        <th className="px-4 py-3 text-right">Stored</th>
                        <th className="px-4 py-3 text-right">Expected</th>
                        <th className="px-4 py-3 text-left">Shown</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.players.map((p) => (
                        <tr key={p.user_id} className="border-t border-white/5">
                          <td className="px-4 py-3 text-white">{p.name}</td>
                          <td className="px-4 py-3 text-slate-400">{p.kind}</td>
                          <td className="px-4 py-3 text-right text-slate-300">{p.roster_count}</td>
                          <td className={`px-4 py-3 text-right ${p.matches ? 'text-slate-300' : 'text-red-300 font-semibold'}`}>
                            {p.stored_points}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-300">{p.expected_points}</td>
                          <td className="px-4 py-3">
                            {p.visible_on_leaderboard ? (
                              <span className="text-emerald-300">yes</span>
                            ) : (
                              <span className="text-amber-300">as removed</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {!detail.players.length && (
                        <tr>
                          <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                            No players hold points or rosters in this season.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </SuperAdminLayout>
  );
}
